<template>
  <div>
    <b-card no-body class="border-secondary rounded p-3">
      <b-navbar>
        <h5 class="m-0 mr-auto">График</h5>
        <b-button id="chart-bar" size="sm"  :variant="config.type === 'bar' ? 'warning' : 'outline-warning'" class="mr-1" @click="setChartType('bar')">
          <font-awesome-icon icon="chart-bar"/>
        </b-button>
        <b-tooltip target="chart-bar" triggers="hover" variant="warning" noninteractive>
          Столбчатый
        </b-tooltip>
        <b-button id="chart-line" size="sm"  :variant="config.type === 'line' ? 'warning' : 'outline-warning'" class="mr-1" @click="setChartType('line')">
          <font-awesome-icon icon="chart-line"/>
        </b-button>
        <b-tooltip target="chart-line" triggers="hover" variant="warning" noninteractive>
          Линейный
        </b-tooltip>
        <b-button id="chart-pie" size="sm"  :variant="config.type === 'pie' ? 'warning' : 'outline-warning'" class="mr-1" @click="setChartType('pie')">
          <font-awesome-icon icon="chart-pie"/>
        </b-button>
        <b-tooltip target="chart-pie" triggers="hover" variant="warning" noninteractive>
          Круговая
        </b-tooltip>
        <b-button variant="outline-warning" size="sm" class="text-dark" @click="openConfig()">
          <font-awesome-icon icon="cogs" class="mr-1"/>
          Настроить
        </b-button>
      </b-navbar>
      <e-charts
        v-if="chartReady && isPerPageAllowed"
        :chart-id="chartId"
        :options="chartOptions"
      />
      <div v-else class="text-center text-muted py-5">
        <font-awesome-icon icon="times-circle" class="mr-1"/>
        Нет данных для отображения или количество записей на странице не поддерживается (максимум 50 записей).
      </div>
    </b-card>

    <b-modal
      id="modal-chart-config"
      ref="modal_chart_config"
      title="Настройка графика"
      size="md"
      button-size="sm"
      ok-title="Применить"
      cancel-title="Назад"
      ok-variant="success"
      centered
      scrollable
      no-close-on-backdrop
      no-close-on-esc
      v-model="showModalConfig"
      @ok="applyConfig()"
    >
      <b-form-group id="xAxis" label="Ось X (Категории)">
        <b-form-select
          v-model="config.xKey"
        >
          <option v-for="f in xFields" :key="f.key" :value="f.key">
            {{ f.label }}
          </option>
        </b-form-select>
        <b-form-checkbox
          class="mt-2"
          v-model="config.isAggregated"
          switch
        >
          Суммировать по одинаковым категориям
        </b-form-checkbox>
      </b-form-group>

      <b-form-group id="yAxis" label="Ось Y (Значения)">
        <b-form-select
          v-model="config.yKey"
        >
          <option v-for="f in yFields" :key="f.key" :value="f.key">
            {{ f.label }}
          </option>
        </b-form-select>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import ECharts from "./ECharts.vue";

export default {
  name: 'EChartBuilder',
  components: { ECharts },
  props: {
    chartId: { type: String, required: true },
    items: { type: Array, required: true },
    fields: { type: Array, required: true },
    initialTitle: { type: String, default: "График" },
    perPage: { type: Number, required: true },
  },
  data() {
    return {
      showModalConfig: false,
      chartOptions: {},
      chartReady: false,
      config: {
        type: 'bar',
        xKey: '',
        yKey: '',
        isAggregated: true,
      }
    }
  },
  computed: {
    xFields() {
      return this.fields.filter(f => f.type === 'category');
    },
    yFields() {
      return this.fields.filter(f => f.type === 'value');
    },
    isPerPageAllowed() {
      return [10, 25, 50].includes(this.perPage);
    },
  },
  mounted() {
    this.initDefaultConfig();
    this.generateChart();
  },
  methods: {
    openConfig() {
      this.$refs.modal_chart_config.show();
      this.showModalConfig = true;
    },
    closeConfig() {
      this.$refs.modal_chart_config.hide();
      this.showModalConfig = false;
    },
    applyConfig() {
      this.generateChart();
      this.$emit('toggle-index', this.config.isAggregated);
      this.closeConfig();
    },
    setChartType(type) {
      this.config.type = type;
      this.generateChart();
    },
    initDefaultConfig () {
      if (this.xFields.length > 0) { this.config.xKey = this.xFields[1].key; }
      if (this.yFields.length > 0) { this.config.yKey = this.yFields[0].key; }
    },
    // Агрегируем данные по одинаковым объектам / id
    getAggregatedData(items, xKey, yKey) {
      return items.reduce((acc, item) => {
        const groupKey = item[xKey];
        const value = parseFloat(item[yKey]);
        const numValue = isNaN(value) ? 0 : value;

        if (!acc[groupKey]) {
          acc[groupKey] = 0;
        }
        acc[groupKey] += numValue;
        return acc;
      }, {});
    },
    generateChart() {
      if (!this.items || this.items.length === 0) { return; }

      const { type: chartType, xKey, yKey, isAggregated } = this.config;
      let yValues = [];
      let xLabels = [];
      const fieldObj = this.fields.find(f => f.key === yKey);
      const yLabels = fieldObj.label;
      const yMeasure = fieldObj.measure;

      if (isAggregated) {
        const aggregatedData = this.getAggregatedData(this.items, xKey, yKey);
        yValues = Object.values(aggregatedData);
        xLabels = Object.keys(aggregatedData);
      } else {
        xLabels = this.items.map((item, index) => `${index + 1}. ${item[xKey]}`);
        yValues = this.items.map(item => {
          const val = parseFloat(item[yKey]);
          return {
            value: isNaN(val) ? 0 : val,
            workTypeName: item['work_type_name']
          }
        });
      }

      const tooltipFormatter = (params) => {
        const p = Array.isArray(params) ? params[0] : params;
        const rawData = p.data;
        const value = (typeof rawData === 'object') ? rawData.value : rawData;

        let workType = '';

        if (rawData.workTypeName) {
          workType = `Вид работ: <b>${rawData.workTypeName}</b>`;
        }

        return `
          ${p.name}
          <br/>
          ${p.marker} ${p.seriesName}: ${value} ${yMeasure}
          <br/>
          ${workType}
        `;
      };

      // Общие настройки графика
      const options = {
        tooltip: {
          trigger: chartType === 'pie' ? 'item' : 'axis',
          axisPointer: { type: 'cross' },
          formatter: tooltipFormatter
        },
        legend: {
          data: [yLabels],
          top: 5
        },
        grid: {
          left: '5%',
          right: '5%',
          bottom: '5%',
          containLabel: true
        },
        toolbox: {
          feature: {
            saveAsImage: {
              title: 'Сохранить график',
              name: yLabels,
              iconStyle: {
                borderColor: '#000',
              }
            }
          }
        },
        dataZoom: [
          {
            type: 'inside',
            start: 0,
            end: 100,
            throttle: 50
          },
          {
            show: true,
            type: 'slider',
            top: '98%',
            start: 0,
            end: 100,
            handleSize: '140%',
            moveHandleSize: 8
          }
        ],
      };

      // Pie chart
      if (chartType === 'pie') {
        const pieData = xLabels.map((label, index) => {
          const item = yValues[index];

          if (typeof item === 'object') {
            return {
              name: label,
              value: item.value,
              workTypeName: item.workTypeName
            };
          }
          return { name: label, value: item };
        });

        this.chartOptions = {
          ...options,
          series: [{
            name: yLabels,
            type: 'pie',
            radius: '50%',
            data: pieData,
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 5,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            },
            animationDuration: 1000
          }]
        };
      } else {
      // Bar & Line charts
        this.chartOptions = {
          ...options,
          xAxis: {
            type: 'category',
            data: xLabels,
            axisLabel: {
              rotate: 30
            }
          },
          yAxis: {
            type: 'value',
            name: yMeasure,
            nameGap: 15,
            nameTextStyle: {
              fontWeight: 'bold',
              fontSize: 14,
              padding: [0, 30, 0, 0]
            }
          },
          series: [{
            name: yLabels,
            type: chartType,
            data: yValues,
            smooth: chartType === 'line',
            itemStyle: {
              color: chartType === 'line' ? '#198754' : '#0d6efd'
            },
            animationDuration: 1000
          }]
        }
      }
      this.chartReady = true;
    },
  }
};
</script>

<style scoped>
#modal-chart-config {
  z-index: 1050;
}
</style>



// WEBPACK FOOTER //
// src/utilites/commonUi/EChartBuilder.vue